import { ArrowDown, ArrowRight, Check } from "lucide-react";
import { GROWTH_STEPS } from "../data/site";
import { SectionHeading } from "./SectionHeading";
import { Reveal } from "./Reveal";

/* Three-stage funnel: Get Found → Get Booked → Get Growing.
   Content lives in GROWTH_STEPS (src/data/site.js). */

const Connector = () => (
    <div className="flex items-center justify-center py-1 lg:py-0" aria-hidden="true">
        <span className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-orange/40 bg-brand-brown-deep text-brand-orange">
            <ArrowDown size={18} className="lg:hidden" />
            <ArrowRight size={18} className="hidden lg:block" />
        </span>
    </div>
);

export const GrowthSystem = () => (
    <section id="growth-system" className="girder-grid-light bg-brand-brown-deep py-16 sm:py-24 lg:py-32" data-testid="growth-system-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
            <SectionHeading
                dark
                chapter="02"
                kicker="The 033 Growth System"
                title="One funnel. Three stages. Zero leaks."
                lead="Customers find you, book you, and come back with others. We build the system for each stage, so each one feeds the next."
                testid="growth-system-heading"
            />

            <ol className="mt-14 grid gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr] lg:items-stretch lg:gap-4">
                {GROWTH_STEPS.map((step, i) => {
                    const featured = Boolean(step.badge);
                    return (
                        <li key={step.num} className="contents">
                            <Reveal delay={i * 0.1} className="h-full">
                                <article
                                    data-testid={`growth-step-${step.num}`}
                                    className={`group flex h-full flex-col rounded-2xl border p-7 transition-colors duration-300 sm:p-8 ${
                                        featured
                                            ? "border-brand-orange/60 bg-brand-brown shadow-[0_24px_60px_-30px_rgba(247,98,9,0.55)]"
                                            : step.status
                                              ? "border-dashed border-brand-cream/25 bg-brand-brown/60"
                                              : "border-brand-cream/15 bg-brand-brown hover:border-brand-orange/50"
                                    }`}
                                >
                                    <div className="flex min-h-[28px] items-center justify-between gap-3">
                                        <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-brand-cream/50">
                                            Stage {step.num}
                                        </span>
                                        {step.badge && (
                                            <span className="rounded-full bg-brand-orange px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white">
                                                {step.badge}
                                            </span>
                                        )}
                                        {step.status && (
                                            <span className="rounded-full border border-dashed border-brand-cream/35 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-brand-cream/75">
                                                {step.status}
                                            </span>
                                        )}
                                    </div>
                                    <h3
                                        className={`mt-5 font-display text-3xl font-bold tracking-tight sm:text-4xl ${
                                            step.status ? "text-brand-orange/70" : "text-brand-orange"
                                        }`}
                                    >
                                        {step.name}
                                    </h3>
                                    <p className="mt-3 font-display text-lg font-semibold leading-snug text-brand-paper">{step.title}</p>
                                    <p className="mt-3 text-sm leading-relaxed text-brand-cream/70">{step.text}</p>

                                    <ul className="mt-6 space-y-3 border-t border-brand-cream/10 pt-6">
                                        {step.items.map((item) => (
                                            <li key={item} className="flex items-start gap-3 text-sm leading-snug text-brand-cream/90">
                                                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-orange/15 text-brand-orange">
                                                    <Check size={13} strokeWidth={3} aria-hidden="true" />
                                                </span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            </Reveal>
                            {i < GROWTH_STEPS.length - 1 && <Connector />}
                        </li>
                    );
                })}
            </ol>
        </div>
    </section>
);
